import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Heart, Plus, Trash2, Lock, Mail, Edit3 } from "lucide-react";
import { pageTransition, fadeUp } from "@/lib/animations";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";

export default function LegacyMessagesPage() {
  const [messages, setMessages] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "",
    content: "",
    message_type: "text",
    recipients: [{ name: "", email: "", relationship: "" }],
  });

  const loadMessages = async () => {
    try {
      const res = await api.get("/legacy");
      setMessages(res.data.messages);
    } catch {
      toast.error("Failed to load legacy messages");
    }
  };

  useEffect(() => { loadMessages(); }, []);

  const addRecipient = () => {
    setForm({ ...form, recipients: [...form.recipients, { name: "", email: "", relationship: "" }] });
  };

  const updateRecipient = (index, field, value) => {
    const updated = [...form.recipients];
    updated[index][field] = value;
    setForm({ ...form, recipients: updated });
  };

  const removeRecipient = (index) => {
    const updated = form.recipients.filter((_, i) => i !== index);
    setForm({ ...form, recipients: updated.length ? updated : [{ name: "", email: "", relationship: "" }] });
  };

  const handleCreate = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and message are required");
      return;
    }
    const validRecipients = form.recipients.filter(r => r.name.trim() && r.email.trim());
    setSaving(true);
    try {
      await api.post("/legacy", { ...form, recipients: validRecipients });
      toast.success("Legacy message sealed");
      setShowNew(false);
      setForm({ title: "", content: "", message_type: "text", recipients: [{ name: "", email: "", relationship: "" }] });
      loadMessages();
    } catch {
      toast.error("Failed to create message");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this legacy message?")) return;
    try {
      await api.delete(`/legacy/${id}`);
      toast.success("Message deleted");
      loadMessages();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <motion.div variants={pageTransition} initial="hidden" animate="visible" className="p-6 md:p-12 max-w-6xl mx-auto" data-testid="legacy-messages-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#00FF88] mb-2 font-medium">Legacy</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Legacy Messages
          </h1>
          <p className="text-sm text-slate-400 mt-1">Sealed messages for your loved ones</p>
        </div>
        <Button
          onClick={() => setShowNew(true)}
          data-testid="legacy-new-btn"
          className="bg-[#00FF88] text-black hover:bg-[#00CC6A] rounded-full px-6 font-bold shadow-[0_0_20px_rgba(0,255,136,0.3)]"
        >
          <Plus className="w-4 h-4 mr-2" /> New Message
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-white/[0.03] backdrop-blur-xl border border-[#00FF88]/10 rounded-2xl p-6 mb-8 flex items-start gap-4">
        <Lock className="w-5 h-5 text-[#00FF88] mt-0.5 shrink-0" />
        <div>
          <p className="text-sm text-white font-medium mb-1">How Legacy Messages Work</p>
          <p className="text-xs text-slate-400 leading-relaxed">
            These messages are sealed and can only be opened by your designated recipients when the time comes.
            Your beneficiary with admin rights will manage the delivery of these messages.
          </p>
        </div>
      </div>

      {messages.length === 0 ? (
        <div className="text-center py-24">
          <Heart className="w-12 h-12 text-slate-700 mx-auto mb-4" />
          <p className="text-slate-500 text-lg">No legacy messages yet</p>
          <p className="text-slate-600 text-sm mt-2">Create your first sealed message for a loved one</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {messages.map((msg, i) => (
            <motion.div
              key={msg.message_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 hover:border-[#00FF88]/10 transition-colors duration-300 group relative"
              data-testid={`legacy-message-${msg.message_id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#00FF88]/10 flex items-center justify-center">
                    <Lock className="w-4 h-4 text-[#00FF88]" />
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-white" style={{ fontFamily: 'Manrope' }}>{msg.title}</h3>
                    <span className="text-xs text-[#00FF88]">Sealed</span>
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(msg.message_id)}
                  data-testid={`legacy-delete-${msg.message_id}`}
                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-slate-500 hover:text-red-400"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <p className="text-sm text-slate-400 line-clamp-3 mb-4">{msg.content}</p>
              {msg.recipients?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {msg.recipients.map((r, ri) => (
                    <span key={ri} className="flex items-center gap-1 text-xs bg-white/5 px-3 py-1 rounded-full text-slate-400">
                      <Mail className="w-3 h-3" /> {r.name || r.email}
                    </span>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-600 mt-4">
                {new Date(msg.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </p>
            </motion.div>
          ))}
        </div>
      )}

      {/* New Message Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-[#111]/95 backdrop-blur-2xl border-white/[0.1] text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Create Legacy Message</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input
              placeholder="Message title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="legacy-title-input"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl h-12"
            />
            <textarea
              placeholder="Write your message..."
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              data-testid="legacy-content-input"
              className="w-full bg-white/5 border border-white/10 focus:border-white/30 text-white placeholder:text-white/30 rounded-xl p-4 h-40 resize-none outline-none focus:ring-1 focus:ring-[#00FF88]/30"
            />

            <div>
              <p className="text-sm font-medium text-white mb-3">Recipients</p>
              {form.recipients.map((r, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <Input
                    placeholder="Name"
                    value={r.name}
                    onChange={(e) => updateRecipient(i, "name", e.target.value)}
                    data-testid={`recipient-name-${i}`}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl h-10 text-sm flex-1"
                  />
                  <Input
                    placeholder="Email"
                    value={r.email}
                    onChange={(e) => updateRecipient(i, "email", e.target.value)}
                    data-testid={`recipient-email-${i}`}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl h-10 text-sm flex-1"
                  />
                  {form.recipients.length > 1 && (
                    <button onClick={() => removeRecipient(i)} className="text-slate-500 hover:text-red-400 px-2">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={addRecipient}
                className="text-xs text-[#00FF88] hover:underline mt-1"
                data-testid="add-recipient-btn"
              >
                + Add another recipient
              </button>
            </div>

            <Button
              onClick={handleCreate}
              disabled={saving}
              data-testid="legacy-save-btn"
              className="w-full bg-[#00FF88] text-black hover:bg-[#00CC6A] rounded-full h-12 font-bold"
            >
              {saving ? "Sealing..." : "Seal Message"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
